import type { ScheduledReportRule } from '@terra/shared-contracts-applications';
import { isEqual } from 'radash';
import {
  createDefaultScheduledReportRule,
  hasScheduledReportRuleFieldErrors,
} from '@terra/ui-design-system';

export type DynamicApplicationScanDraft = {
  enabled: boolean;
  scheduleRule: ScheduledReportRule | null;
  vulnerabilityEnabled: boolean;
  directoryEnabled: boolean;
};

export type DynamicApplicationScanBaseline = {
  enabled: boolean;
  scheduleRule: ScheduledReportRule | null;
  vulnerabilityEnabled: boolean;
  directoryEnabled: boolean;
  hasUrl: boolean;
  hasScanTargets: boolean;
  nextRunAt: Date | string | null;
};

type ComparableScheduleRule = {
  frequency: ScheduledReportRule['frequency'];
  daysOfWeek: number[];
  dayOfMonth: number | null;
  lastDayOfMonth: boolean | null;
  fiscalYearStartMonth: number | null;
  yearlyMonth: number | null;
  yearlyDay: number | null;
  hour: number;
  timezone: string;
};

export const createDefaultDynamicApplicationScanDraft =
  (): DynamicApplicationScanDraft => ({
    enabled: false,
    scheduleRule: null,
    vulnerabilityEnabled: true,
    directoryEnabled: false,
  });

export const dynamicApplicationScanDraftFromBaseline = (
  baseline: DynamicApplicationScanBaseline,
): DynamicApplicationScanDraft => ({
  enabled: baseline.enabled,
  scheduleRule: baseline.scheduleRule,
  vulnerabilityEnabled: baseline.vulnerabilityEnabled,
  directoryEnabled: baseline.directoryEnabled,
});

const toComparableScheduleRule = (
  rule: ScheduledReportRule | null,
): ComparableScheduleRule | null => {
  if (!rule) {
    return null;
  }

  return {
    frequency: rule.frequency,
    daysOfWeek: [...rule.daysOfWeek].sort((left, right) => left - right),
    dayOfMonth: rule.dayOfMonth ?? null,
    lastDayOfMonth: rule.lastDayOfMonth ?? null,
    fiscalYearStartMonth: rule.fiscalYearStartMonth ?? null,
    yearlyMonth: rule.yearlyMonth ?? null,
    yearlyDay: rule.yearlyDay ?? null,
    hour: rule.hour,
    timezone: rule.timezone,
  };
};

export const areDynamicApplicationScanDraftsEqual = (
  left: DynamicApplicationScanDraft,
  right: DynamicApplicationScanDraft,
): boolean =>
  left.enabled === right.enabled &&
  left.vulnerabilityEnabled === right.vulnerabilityEnabled &&
  left.directoryEnabled === right.directoryEnabled &&
  isEqual(
    toComparableScheduleRule(left.scheduleRule),
    toComparableScheduleRule(right.scheduleRule),
  );

export const isDynamicApplicationScanDraftPersistable = (
  draft: DynamicApplicationScanDraft,
): boolean => {
  if (!draft.enabled) {
    return true;
  }

  if (!draft.scheduleRule) {
    return false;
  }

  return !hasScheduledReportRuleFieldErrors(draft.scheduleRule);
};

export const enableDynamicApplicationScanDraft = (
  current: DynamicApplicationScanDraft,
): DynamicApplicationScanDraft => {
  if (current.scheduleRule != null) {
    return { ...current, enabled: true };
  }

  return {
    ...current,
    enabled: true,
    scheduleRule: createDefaultScheduledReportRule({ frequency: 'WEEKLY' }),
  };
};

export const disableDynamicApplicationScanDraft = (
  current: DynamicApplicationScanDraft,
): DynamicApplicationScanDraft => ({
  ...current,
  enabled: false,
  scheduleRule: null,
});

export const toDynamicApplicationScanUpdatePayload = (
  draft: DynamicApplicationScanDraft,
) => ({
  dynamicApplicationScanEnabled: draft.enabled,
  dynamicApplicationScanScheduleRule: draft.enabled ? draft.scheduleRule : null,
  dynamicApplicationScanVulnerabilityEnabled: draft.vulnerabilityEnabled,
  dynamicApplicationScanDirectoryEnabled: draft.directoryEnabled,
});
